import React from "react";
import { Button, Col, Form, Input, Modal, Row, Select, Spin } from "antd";
import { CheckSquareOutlined } from "@ant-design/icons";
import { TacheService } from "../../services/TacheService";
import Toast from "../../helpers/Toast";
import PropTypes from "prop-types";
import CustomFormItem from "../utils/form/CustomFormItem";
import { AiNameTrigger, AiNamePanel } from "../ai/AiNameSuggest";
import { useAiNameAssist } from "../../hooks/useAiNameAssist";
import AiDescriptionSuggest from "../ai/AiDescriptionSuggest";
import style from "./TacheCreateForm.module.scss";
import { catchError } from "../../services/DaoService.js";
import { AffaireService } from "../../services/AffaireService.js";
import { tachesState } from "../../state/actions/tachesState.js";

const TacheCreateForm = ({ visible, onCreate, onCancel, affaire }) => {
    const [form] = Form.useForm();
    const nomValue = Form.useWatch('nom', form);
    const nameAi = useAiNameAssist('tache');
    const [creating, setCreating] = React.useState(false);
    const [validationErrors, setValidationErrors] = React.useState({});
    const [loading, setLoading] = React.useState(true);

    const [affaires, setAffaires] = React.useState([]);

    React.useEffect(() => {
        if (!visible) return;
        setLoading(true);
        AffaireService.getInstance().all()
            .then(r => setAffaires(r.data))
            .catch(catchError)
            .finally(() => setLoading(false));
    }, [visible]);

    const doCreate = (values) => {
        return TacheService.getInstance()
            .create(values)
            .then((response) => {
                Toast.success(response.message);
                tachesState.addTache(response.data);
                return response.data;
            })
            .catch((error) => {
                const errors = error.response?.data;
                if (errors?.validation && Object.keys(errors.errors).length) {
                    setValidationErrors(errors.errors);
                } else {
                    catchError(error);
                }
                return null;
            });
    };

    const handleSave = () => {
        setCreating(true);
        form.validateFields().then(async (values) => {
            if (affaire) values.affaire_id = affaire.id;
            const data = await doCreate(values);
            if (data) {
                form.resetFields();
                onCreate(data);
            }
        }).catch(() => {}).finally(() => setCreating(false));
    };

    const handleSaveAndNew = () => {
        setCreating(true);
        form.validateFields().then(async (values) => {
            if (affaire) values.affaire_id = affaire.id;
            const data = await doCreate(values);
            if (data) {
                form.resetFields();
                setValidationErrors({});
            }
        }).catch(() => {}).finally(() => setCreating(false));
    };

const TitleNode = (
        <div className={style.modalTitle}>
            <span className={style.titleIcon}>
                <CheckSquareOutlined />
            </span>
            <div>
                <span className={style.titleText}>Nouvelle tâche</span>
                <span className={style.titleSubtitle}>
                    Renseignez les informations pour créer et suivre cette tâche
                </span>
            </div>
        </div>
    );

    const FooterNode = (
        <div className={style.modalFooter}>
            <Button className={style.cancelBtn} onClick={onCancel}>
                Annuler
            </Button>
            <Button
                className={style.submitBtnSecondary}
                loading={creating}
                onClick={handleSaveAndNew}
            >
                Enregistrer et créer autre
            </Button>
            <Button
                type="primary"
                className={style.submitBtn}
                icon={<CheckSquareOutlined />}
                loading={creating}
                onClick={handleSave}
            >
                Enregistrer
            </Button>
        </div>
    );

    return (
        <Modal
            width={560}
            centered
            className={style.tacheModal}
            wrapClassName={style.tacheModalWrap}
            destroyOnHidden
            open={visible}
            onCancel={onCancel}
            title={TitleNode}
            footer={FooterNode}
        >
            <Spin spinning={loading}>
                <Form layout="vertical" form={form} name="createTacheForm" className={style.form}>
                    <Row gutter={16}>
                        <Col xs={24}>
                            <CustomFormItem
                                label="Nom"
                                name="nom"
                                rules={[{ required: true, message: "Veuillez saisir le nom!" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input
                                    className={style.control}
                                    placeholder="Intitulé de la tâche"
                                    autoFocus
                                    suffix={
                                        <AiNameTrigger
                                            loading={nameAi.loading}
                                            disabled={!nomValue || nomValue.trim().length < 3}
                                            onClick={() => nameAi.run(form.getFieldValue('nom'))}
                                        />
                                    }
                                />
                            </CustomFormItem>
                            <AiNamePanel
                                status={nameAi.status}
                                proposal={nameAi.proposal}
                                onApply={(text) => { form.setFieldsValue({ nom: text }); nameAi.reset(); }}
                                onReset={nameAi.reset}
                            />
                        </Col>

                        {!affaire && (
                            <Col xs={24}>
                                <CustomFormItem
                                    label="Dossier (optionnel)"
                                    name="affaire_id"
                                    validationErrors={validationErrors}
                                    setValidationErrors={setValidationErrors}
                                >
                                    <Select
                                        allowClear
                                        className={style.control}
                                        optionFilterProp="label"
                                        placeholder="Rattacher à un dossier"
                                        showSearch
                                        loading={loading}
                                        options={affaires.map((a) => ({ label: a.nom, value: a.id }))}
                                    />
                                </CustomFormItem>
                            </Col>
                        )}

                        <Col xs={24}>
                            <CustomFormItem
                                label="Description (optionnel)"
                                name="description"
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input.TextArea
                                    rows={3}
                                    className={style.textarea}
                                    placeholder="Décrivez l'objectif ou le contexte de cette tâche…"
                                />
                            </CustomFormItem>
                            <AiDescriptionSuggest
                                type="tache"
                                getNom={() => form.getFieldValue('nom')}
                                getCurrent={() => form.getFieldValue('description')}
                                onApply={(text) => form.setFieldsValue({ description: text })}
                            />
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    );
};

export default TacheCreateForm;

TacheCreateForm.propTypes = {
    visible: PropTypes.bool,
    onCreate: PropTypes.func,
    onCancel: PropTypes.func,
    affaire: PropTypes.object,
};
